import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import LoadingButton from '@tracker/shared/src/LoadingIndicator/LoadingButton';
import {makeRequired, makeValidate, TextField} from 'mui-rff';
import React, {useCallback, useMemo} from 'react';
import {Form} from 'react-final-form';
import * as Yup from 'yup';
import {BookmarkLinkGroupWithId, linkIdGenerator} from './Home';

type Props = {
    open : boolean;
    linkGroup ?: BookmarkLinkGroupWithId;
    onSubmit : (linkGroup : BookmarkLinkGroupWithId) => Promise<void>;
    onClose : () => void;
};

const schema = Yup.object().shape({
    name: Yup.string().label('Name').required().trim(),
}).required();

type FormData = Yup.InferType<typeof schema>;

const validate = makeValidate(schema);
const required = makeRequired(schema);

const LinkGroupFormDialog : React.FC<Props> = ({open, linkGroup, onSubmit, onClose} : Props) => {
    const handleSubmit = useCallback(async (values : FormData) => {
        const data = schema.cast(values);
        await onSubmit({
            id: linkGroup ? linkGroup.id : linkIdGenerator(),
            links: linkGroup ? linkGroup.links : [],
            ...data,
        });
        onClose();
    }, [onSubmit, onClose, linkGroup]);

    const initialValues = useMemo(() => linkGroup ? linkGroup : undefined, [linkGroup]);

    return (
        <Form
            subscription={{submitting: true, pristine: true}}
            onSubmit={handleSubmit}
            validate={validate}
            initialValues={initialValues}
            render={({handleSubmit, pristine, submitting, form: {reset}}) => (
                <Dialog
                    open={open}
                    onExited={() => reset()}
                    disableEscapeKeyDown={submitting}
                    onClose={(_event, reason) => {
                        if(reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                            onClose();
                        }
                    }}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        component: 'form',
                        onSubmit: handleSubmit,
                        // @ts-ignore Workaround for https://github.com/mui-org/material-ui/issues/13253
                        noValidate: true,
                    }}
                >
                    <DialogTitle>{linkGroup ? 'Edit' : 'Create'} Link Group</DialogTitle>
                    <DialogContent>
                        <TextField
                            variant="outlined"
                            label="Name"
                            name="name"
                            required={required.name}
                            autoFocus
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => onClose()} disabled={submitting}>Cancel</Button>
                        <LoadingButton
                            loading={submitting}
                            type="submit"
                            color="primary"
                            disabled={pristine || submitting}
                        >
                            {linkGroup ? 'Update' : 'Create'}
                        </LoadingButton>
                    </DialogActions>
                </Dialog>
            )}
        />
    );
};

export default LinkGroupFormDialog;
